import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router";
import { slugify } from "@/utils";
import { chatService, interestsService } from "@/services";
import {
  BadgeList,
  ModalAlert,
  PremiumFloating,
  UserCard,
  UserCardData,
  WaitingApproval,
} from "@/components";
import { useInterests, useUser } from "@/contexts";
import {
  useCardLock,
  useHydratedUser,
  usePermissions,
  usePresenceMap,
} from "@/hooks";
import "./Interests.css";

const TAB_LABELS = {
  visitors: "Me viu",
  favorites: "Favoritos",
  favoritedMe: "Me favoritou",
} as const;

type TabKey = keyof typeof TAB_LABELS;
const PAGE_SIZE = 12;

const InterestsScreen: React.FC = () => {
  const { user: currentUser, userData, excludedUserIds } = useUser();
  const { favoritesGiven, favoritesReceived, visitors, interestsLoading } =
    useInterests();
  const navigate = useNavigate();
  const { getUser } = useHydratedUser();
  const { hasPermission } = usePermissions();
  const presenceMap = usePresenceMap();

  const { isCardLocked } = useCardLock(userData);

  const favoritesSet = useMemo(() => {
    return new Set(favoritesGiven?.map((f) => f.targetUser));
  }, [favoritesGiven]);

  const [activeTab, setActiveTab] = useState<TabKey>("visitors");
  const [favoriteBusyId, setFavoriteBusyId] = useState<string | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const [hydratedVisitors, setHydratedVisitors] = useState<UserCardData[]>([]);
  const [hydratedFavorites, setHydratedFavorites] = useState<UserCardData[]>(
    [],
  );
  const [hydratedFavoritedMe, setHydratedFavoritedMe] = useState<
    UserCardData[]
  >([]);

  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const excluded = useMemo(
    () => new Set(excludedUserIds || []),
    [excludedUserIds],
  );

  useEffect(() => {
    const run = async () => {
      const result = await Promise.all(
        (visitors || []).map(async (item: any) => {
          const profile = await getUser(item.viewerId);

          return {
            uid: item.viewerId,
            username: profile?.username || item.viewerData?.username,
            usernameSlug: slugify(
              profile?.username || item.viewerData?.username || "",
            ),
            birthYear: profile?.birthYear || item.viewerData?.birthYear || "",
            city: profile?.city || item.viewerData?.city || "",
            state: profile?.state || item.viewerData?.state || "",
            profileImage:
              profile?.profileImage || item.viewerData?.profileImage || "",
            photosCount:
              profile?.photosCount || item.viewerData?.photosCount || 0,
            gender: profile?.gender || item.viewerData?.gender || "",
            planInfo: profile?.planInfo || null,
          };
        }),
      );

      setHydratedVisitors(result);
    };

    run();
  }, [visitors]);

  useEffect(() => {
    const run = async () => {
      const result = await Promise.all(
        (favoritesGiven || []).map(async (item: any) => {
          const profile = await getUser(item.targetUser);

          return {
            uid: item.targetUser,
            username: profile?.username || item.targetUserData?.username,
            usernameSlug: slugify(
              profile?.username || item.targetUserData?.username || "",
            ),
            birthYear:
              profile?.birthYear || item.targetUserData?.birthYear || "",
            city: profile?.city || item.targetUserData?.city || "",
            state: profile?.state || item.targetUserData?.state || "",
            profileImage:
              profile?.profileImage || item.targetUserData?.profileImage || "",
            photosCount:
              profile?.photosCount || item.targetUserData?.photosCount || 0,
            gender: profile?.gender || item.targetUserData?.gender || "",
            planInfo: profile?.planInfo || null,
          };
        }),
      );

      setHydratedFavorites(result);
    };

    run();
  }, [favoritesGiven]);

  useEffect(() => {
    const run = async () => {
      const result = await Promise.all(
        (favoritesReceived || []).map(async (item: any) => {
          const profile = await getUser(item.favoritedBy);

          return {
            uid: item.favoritedBy,
            username: profile?.username || item.favoritorData?.username,
            usernameSlug: slugify(
              profile?.username || item.favoritorData?.username || "",
            ),
            birthYear:
              profile?.birthYear || item.favoritorData?.birthYear || "",
            city: profile?.city || item.favoritorData?.city || "",
            state: profile?.state || item.favoritorData?.state || "",
            profileImage:
              profile?.profileImage || item.favoritorData?.profileImage || "",
            photosCount:
              profile?.photosCount || item.favoritorData?.photosCount || 0,
            gender: profile?.gender || item.favoritorData?.gender || "",
            planInfo: profile?.planInfo || null,
          };
        }),
      );

      setHydratedFavoritedMe(result);
    };

    run();
  }, [favoritesReceived]);

  const currentCards = useMemo(() => {
    const tabs = {
      visitors: hydratedVisitors,
      favorites: hydratedFavorites,
      favoritedMe: hydratedFavoritedMe,
    };

    return tabs[activeTab] || [];
  }, [activeTab, hydratedVisitors, hydratedFavorites, hydratedFavoritedMe]);

  const favoriteIds = useMemo(
    () => new Set(hydratedFavorites.map((f) => f.uid)),
    [hydratedFavorites],
  );

  const visibleCards = useMemo(
    () => currentCards.slice(0, visibleCount),
    [currentCards, visibleCount],
  );

  const hasMore = visibleCount < currentCards.length;

  const tabOptions = useMemo(() => {
    return [
      {
        key: "visitors" as TabKey,
        label: TAB_LABELS.visitors,
        count: hydratedVisitors.length,
        permission: null,
      },
      {
        key: "favorites" as TabKey,
        label: TAB_LABELS.favorites,
        count: hydratedFavorites.length,
        permission: null,
      },
      {
        key: "favoritedMe" as TabKey,
        label: TAB_LABELS.favoritedMe,
        count: hydratedFavoritedMe.length,
        permission: null,
      },
    ];
  }, [
    hydratedVisitors.length,
    hydratedFavorites.length,
    hydratedFavoritedMe.length,
  ]);

  const handleFavorite = useCallback((user: UserCardData) => {
    handleToggleFavorite(user);
  }, []);

  const handleToggleFavorite = async (targetUser: UserCardData) => {
    if (!currentUser || !userData || !targetUser.uid) return;

    const isSubscriber = hasPermission("LIKE_PROFILES");

    if (!isSubscriber) {
      setAlertMessage("Ops! Você precisa ser assinante para favoritar perfis.");
      setIsAlertOpen(true);
      return;
    }

    setFavoriteBusyId(targetUser.uid);

    try {
      await interestsService.toggleFavorite(
        currentUser,
        userData,
        targetUser,
        isSubscriber,
      );
    } catch (e) {
      console.error(e);
    } finally {
      setFavoriteBusyId(null);
    }
  };

  const handleRequestChat = async (targetUser: UserCardData) => {
    if (!userData || !currentUser) return;

    const isSubscriber = hasPermission("ACCESS_CHAT_AREA");

    if (!isSubscriber) {
      setAlertMessage("Você precisa ser assinante para enviar mensagens.");
      setIsAlertOpen(true);
      return;
    }

    try {
      const chatId = await chatService.createOrGetConversation(
        currentUser.uid,
        userData,
        targetUser.uid,
        targetUser,
        isSubscriber,
      );

      navigate("/mensagens", { state: { selectedChatId: chatId } });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeTab]);

  useEffect(() => {
    if (!hasMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + PAGE_SIZE, currentCards.length),
          );
        }
      },
      { rootMargin: "200px" },
    );

    if (sentinelRef.current) observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [hasMore, currentCards.length]);

  if (userData?.awaitingApproval) {
    return (
      <div className="feed-page container">
        <WaitingApproval username={userData.username || ""} />
      </div>
    );
  }

  return (
    <div className="interests-page container">
      <div className="interests-header">
        <BadgeList
          tabs={tabOptions}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      <div className="interests-grid">
        {visibleCards.map((card, index) => (
          <UserCard
            key={`${card.uid}-${index}`}
            data={card}
            isLocked={isCardLocked(card)}
            isFavorited={favoritesSet.has(card.uid)}
            presence={presenceMap[card.uid]}
            selected={selectedCardId === card.uid}
            favoriteBusyId={favoriteBusyId}
            onNavigate={() => {
              navigate("/perfil/" + slugify(card.username));
            }}
            onSelect={(uid) => {
              setSelectedCardId((prev) => (prev === uid ? null : uid));
            }}
            onStartChat={handleRequestChat}
            onFavorite={handleFavorite}
          />
        ))}

        <div ref={sentinelRef} style={{ height: "20px", gridColumn: "1/-1" }} />
      </div>

      {!interestsLoading && currentCards.length === 0 && (
        <div className="results-empty-interests results-empty-full">
          Nenhum resultado
        </div>
      )}

      {!userData?.planInfo?.active && (
        <PremiumFloating navigate={() => navigate("/planos")} />
      )}

      <ModalAlert
        title={alertMessage || "Ops!"}
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onSubscribe={() => {
          navigate("/planos");
          setIsAlertOpen(false);
        }}
      />
    </div>
  );
};

export default InterestsScreen;
